"use client";
import { useActionState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@simurgh/feathers/ui/button";
import {
	ModalBody,
	ModalContent,
	ModalHeader,
} from "@simurgh/feathers/ui/modal";
import { Input } from "@simurgh/feathers/ui/input";
import { Form } from "@simurgh/feathers/ui/form";
import { TABLE } from "../lib/shared";
import { DeckSchema, type Deck, type DeckIdentifiable } from "../model";
import { addToast } from ":shared/lib/toast";
import { updateRecord } from "../lib/update";
import type { DatabaseProperties } from ":shared/database/database-properties";
import { Icon } from ":shared/ui/icon";
import { TextField } from "@simurgh/feathers/ui/text-field";
import { Label } from "react-aria-components";
import { FieldError } from "@simurgh/feathers/ui/field";
import { useDisclosure } from "@simurgh/feathers/lib/use-disclosure";

type FormState = {
	success: boolean;
	error?: string;
	deck?: Deck;
};

export function UpdateRecordButton({
	recordId,
	record,
	className,
}: {
	recordId: DatabaseProperties["id"];
	record: DeckIdentifiable;
	className?: string;
}) {
	const queryClient = useQueryClient();
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [state, formAction, isPending] = useActionState(
		async (
			_previousState: FormState,
			formData: FormData,
		): Promise<FormState> => {
			const title = formData.get("title") as string;
			const description = formData.get("description") as string;

			// Validate using Zod schema
			const validation = DeckSchema.safeParse({ title, description });

			if (!validation.success) {
				const firstError = validation.error.issues[0];
				return {
					success: false,
					error: firstError?.message || "Validation failed",
				};
			}

			try {
				const response = await updateRecord(recordId, validation.data);

				queryClient.invalidateQueries({ queryKey: [TABLE] });
				queryClient.invalidateQueries({ queryKey: [TABLE, recordId] });
				addToast({
					title: `${response.title} updated successfully`,
				});

				// Close modal on success
				onClose();

				return {
					success: true,
					deck: response,
				};
			} catch (error) {
				const message =
					(error as Error)?.message ||
					"Failed to update deck. Please try again.";
				addToast({
					title: "Update deck failed",
					description: message,
					color: "danger",
				});
				return {
					success: false,
					error: message,
				};
			}
		},
		{ success: false },
	);

	return (
		<>
			<Button
				size="sq-md"
				aria-label={`Update ${TABLE}`}
				onPress={onOpen}
				intent="plain"
				className={className}
			>
				<Icon name="hugeicons-pencil-edit-01" size={24} />
			</Button>
			<ModalContent isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
				<ModalHeader>Update {TABLE}</ModalHeader>
				<ModalBody className="text-base">
					<Form action={formAction} className="space-y-4">
						<TextField isRequired isDisabled={isPending}>
							<Label>Title</Label>
							<Input type="text" name="title" defaultValue={record.title} />
							<FieldError />
						</TextField>
						<TextField isDisabled={isPending}>
							<Label>Description</Label>
							<Input
								type="text"
								name="description"
								defaultValue={record.description}
							/>
							<FieldError />
						</TextField>
						{state.error &&
							!state.error.includes("title") &&
							!state.error.includes("description") && (
								<p className="text-sm text-danger">{state.error}</p>
							)}
						<div className="flex items-center justify-end gap-2 w-full mt-4">
							<Button
								type="button"
								intent="secondary"
								onPress={onClose}
								isDisabled={isPending}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								intent="primary"
								isDisabled={isPending}
								isPending={isPending}
							>
								Update
							</Button>
						</div>
					</Form>
				</ModalBody>
			</ModalContent>
		</>
	);
}
